import { useEffect, useState, useCallback } from "react";
import { Link, useLocation } from "react-router-dom";
import { ArrowLeft, ChevronLeft, ChevronRight, Search, Loader2, Download, List, Wallet } from "lucide-react";
import EntityPicker from "../components/EntityPicker";
import RegisterCommissionPaymentModal from "../components/RegisterCommissionPaymentModal";
import { adminApi } from "../api";

const EUR = (v) => (Number(v) || 0).toLocaleString("pt-PT", { style: "currency", currency: "EUR" });

function toCsv(rows, cols) {
  if (!rows.length) return "";
  const header = cols.map((c) => `"${c.label}"`).join(";");
  const body = rows.map((r) =>
    cols.map((c) => {
      const raw = c.accessor(r);
      const s = raw === null || raw === undefined ? "" : String(raw);
      return `"${s.replace(/"/g, '""')}"`;
    }).join(";"),
  ).join("\n");
  return "\uFEFF" + header + "\n" + body;
}

function downloadCsv(filename, content) {
  const blob = new Blob([content], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url; a.download = filename; a.click();
  URL.revokeObjectURL(url);
}

// Report config registry ----------------------------------------------
const REPORTS = {
  "clients-by-product": {
    title: "Clientes por Produto",
    subtitle: "Linhas de encomenda que contêm o produto.",
    slug: "clients-by-product",
    paramKey: "product_id",
    paramLabel: "Produto",
    paramEntity: "produto",
    cols: [
      { key: "ID_Encomenda", label: "Encomenda", accessor: (r) => (
        <Link to={`/encomendas/${r.ID_Encomenda}`}
          data-testid={`report-order-link-${r.ID_Encomenda}`}
          className="hover:underline text-[#0F1829]">#{r.ID_Encomenda}</Link>
      ), mono: true },
      { key: "DataEncomenda", label: "Data", accessor: (r) => r.DataEncomenda, mono: true },
      { key: "EstadoEncomenda", label: "Situação", accessor: (r) => r.EstadoEncomenda || r.ID_EstadoEncomenda, mono: true },
      { key: "Quantidade", label: "Qtd.", accessor: (r) => r.Quantidade, mono: true, align: "right" },
      { key: "Total", label: "Total", accessor: (r) => EUR(r.Total), mono: true, align: "right" },
      { key: "Cliente", label: "Cliente", accessor: (r) => (
        <span>
          <span className="font-medium">{r.NomeCompleto}</span>
          <span className="ml-1.5 text-[10px] uppercase tracking-[0.1em] px-1.5 py-0.5 rounded bg-[#F5F2EB] border border-[#E6E1D6] text-[#6B7280]">{r.TipoCliente || r.ID_TipoCliente}</span>
        </span>
      ) },
      { key: "Email", label: "Email", accessor: (r) => r.Email },
      { key: "Telemovel", label: "Telemóvel", accessor: (r) => r.Telemovel || "—", mono: true },
    ],
  },
  "clients-by-pack": {
    title: "Clientes por Pack",
    subtitle: "Linhas de encomenda que contêm o pack.",
    slug: "clients-by-pack",
    paramKey: "pack_id",
    paramLabel: "Pack",
    paramEntity: "pack",
    cols: [
      { key: "ID_Encomenda", label: "Encomenda", accessor: (r) => (
        <Link to={`/encomendas/${r.ID_Encomenda}`}
          data-testid={`report-pack-order-link-${r.ID_Encomenda}`}
          className="hover:underline text-[#0F1829]">#{r.ID_Encomenda}</Link>
      ), mono: true },
      { key: "DataEncomenda", label: "Data", accessor: (r) => r.DataEncomenda, mono: true },
      { key: "EstadoEncomenda", label: "Situação", accessor: (r) => r.EstadoEncomenda || r.ID_EstadoEncomenda, mono: true },
      { key: "Quantidade", label: "Qtd.", accessor: (r) => r.Quantidade, mono: true, align: "right" },
      { key: "Total", label: "Total", accessor: (r) => EUR(r.Total), mono: true, align: "right" },
      { key: "Cliente", label: "Cliente", accessor: (r) => (
        <span>
          <span className="font-medium">{r.NomeCompleto}</span>
          <span className="ml-1.5 text-[10px] uppercase tracking-[0.1em] px-1.5 py-0.5 rounded bg-[#F5F2EB] border border-[#E6E1D6] text-[#6B7280]">{r.TipoCliente || r.ID_TipoCliente}</span>
        </span>
      ) },
      { key: "Email", label: "Email", accessor: (r) => r.Email },
      { key: "Telemovel", label: "Telemóvel", accessor: (r) => r.Telemovel || "—", mono: true },
    ],
  },
  "commissions-client": {
    title: "Posição de Comissões",
    subtitle: "Totais de comissão por cliente: ganhas, pagas, por pagar e disponíveis.",
    slug: "client-commissions",
    // Optional cliente + tipo filters (no hard param required)
    extraFilters: [
      { key: "client_id", label: "Cliente", type: "entity", entity: "cliente" },
      { key: "tipo_cliente", label: "Tipo Cliente", type: "entity", entity: "tipo_cliente" },
    ],
    cols: [
      { key: "Cliente", label: "Cliente", accessor: (r) => (
        <span>
          <span className="font-mono text-[11px] text-[#978D78] mr-1.5">#{r.ID_Cliente}</span>
          <span className="font-medium">{r.NomeCompleto}</span>
          <span className="ml-1.5 text-[10px] uppercase tracking-[0.1em] px-1.5 py-0.5 rounded bg-[#F5F2EB] border border-[#E6E1D6] text-[#6B7280]">{r.TipoCliente || r.ID_TipoCliente}</span>
        </span>
      ) },
      { key: "Email", label: "Email", accessor: (r) => r.Email },
      { key: "ValorTotal", label: "Total Comissões", accessor: (r) => EUR(r.ValorTotal), mono: true, align: "right" },
      { key: "ValorPago", label: "Pago", accessor: (r) => EUR(r.ValorPago), mono: true, align: "right" },
      { key: "UltimoPagamento", label: "Último Pag.", accessor: (r) => r.UltimoPagamento || "—", mono: true },
      { key: "PorPagar", label: "Por Pagar", accessor: (r) => EUR(r.PorPagar), mono: true, align: "right" },
      { key: "DisponivelPagamento", label: "Disponível Pag.", accessor: (r) => EUR(r.DisponivelPagamento), mono: true, align: "right" },
    ],
    rowActions: ({ row, refresh }) => (
      <RegisterCommissionButton client={row} onDone={refresh} />
    ),
  },
  "consultant-levels": {
    title: "Níveis de Negócio",
    subtitle: "Níveis mensais por consultor (NetPoints, Bónus, Nível).",
    slug: "consultant-levels",
    extraFilters: [
      { key: "client_id", label: "Cliente", type: "entity", entity: "cliente" },
      { key: "year", label: "Ano", type: "number", placeholder: "YYYY" },
      { key: "month", label: "Mês", type: "number", placeholder: "1-12" },
    ],
    cols: [
      { key: "ID_Cliente", label: "ID Cliente", accessor: (r) => r.ID_Cliente, mono: true },
      { key: "NomeCompleto", label: "Cliente", accessor: (r) => r.NomeCompleto },
      { key: "Ano", label: "Ano", accessor: (r) => r.Ano, mono: true },
      { key: "Mes", label: "Mês", accessor: (r) => r.Mes, mono: true },
      { key: "NetPointsPessoal", label: "NPTS Pessoal", accessor: (r) => Number(r.NetPointsPessoal || 0).toFixed(2), mono: true, align: "right" },
      { key: "NetPointsCliente", label: "NPTS Cliente", accessor: (r) => Number(r.NetPointsCliente || 0).toFixed(2), mono: true, align: "right" },
      { key: "NetPointsRede", label: "NPTS Rede", accessor: (r) => Number(r.NetPointsRede || 0).toFixed(2), mono: true, align: "right" },
      { key: "NetPointsTotal", label: "NPTS Total", accessor: (r) => Number(r.NetPointsTotal || 0).toFixed(2), mono: true, align: "right" },
      { key: "Bonus", label: "Bónus", accessor: (r) => `${Number(r.Bonus || 0).toFixed(0)}%`, mono: true, align: "right" },
      { key: "Nivel", label: "Nível", accessor: (r) => r.Nivel || "—", mono: true },
      { key: "NetworksTop", label: "Networks TOP", accessor: (r) => r.NetworksTop ?? 0, mono: true, align: "right" },
      { key: "PinNegocio", label: "Pin de Negócio", accessor: (r) => (
        r.PinNegocioPin
          ? (
            <span className="inline-flex items-center gap-1 text-[10px]">
              <span className="font-mono font-semibold">{r.PinNegocioPin}</span>
              <span className="text-[#6B7280]">·</span>
              <span>{r.PinNegocioNivel}</span>
              <span className="text-[#6B7280]">·</span>
              <span className="font-mono">{r.PinNegocioBonus}%</span>
            </span>
          )
          : <span className="text-[#9CA3AF]">—</span>
      ) },
    ],
  },
  "new-consultant-levels": {
    title: "Novos Níveis de Negócio",
    subtitle: "Consultores que atingiram um novo nível máximo no mês escolhido.",
    slug: "new-consultant-levels",
    extraFilters: [
      { key: "year", label: "Ano", type: "number", placeholder: "YYYY" },
      { key: "month", label: "Mês", type: "number", placeholder: "1-12" },
    ],
    cols: [
      { key: "ID_Cliente", label: "ID Cliente", accessor: (r) => r.ID_Cliente, mono: true },
      { key: "NomeCompleto", label: "Cliente", accessor: (r) => r.NomeCompleto },
      { key: "Ano", label: "Ano", accessor: (r) => r.Ano, mono: true },
      { key: "Mes", label: "Mês", accessor: (r) => r.Mes, mono: true },
      { key: "Bonus", label: "Bónus", accessor: (r) => `${Number(r.Bonus || 0).toFixed(0)}%`, mono: true, align: "right" },
      { key: "Nivel", label: "Nível", accessor: (r) => r.Nivel || "—", mono: true },
    ],
  },
  "new-pins": {
    title: "Novos PINs no Trimestre",
    subtitle: "Consultores cujo Pin Máximo atingido no trimestre é superior ao anterior.",
    slug: "new-consultant-pins",
    extraFilters: [
      { key: "year", label: "Ano", type: "number", placeholder: "YYYY" },
      { key: "trimestre", label: "Trimestre", type: "number", placeholder: "1-4" },
    ],
    cols: [
      { key: "ID_Cliente", label: "ID Cliente", accessor: (r) => r.ID_Cliente, mono: true },
      { key: "NomeCompleto", label: "Cliente", accessor: (r) => r.NomeCompleto },
      { key: "PinMaximoAntigo", label: "Pin Máximo Antigo", accessor: (r) => r.PinMaximoAntigo != null ? `${Number(r.PinMaximoAntigo).toFixed(0)}% · ${r.NivelAntigo || '—'}` : "—", mono: true },
      { key: "PinMaximo", label: "Pin Máximo", accessor: (r) => `${Number(r.PinMaximo || 0).toFixed(0)}% · ${r.NivelMaximo || '—'}`, mono: true },
    ],
  },
  "clients-volume": {
    title: "Volume por Cliente",
    subtitle: "Volume de compras pessoais por cliente.",
    slug: "clients-volume",
    extraFilters: [
      { key: "client_id", label: "Cliente", type: "entity", entity: "cliente" },
      { key: "tipo_cliente", label: "Tipo Cliente", type: "entity", entity: "tipo_cliente" },
      { key: "year", label: "Ano", type: "number", placeholder: "YYYY" },
    ],
    cols: [
      { key: "Cliente", label: "Cliente", accessor: (r) => (
        <span>
          <span className="font-mono text-[11px] text-[#978D78] mr-1.5">#{r.ID_Cliente}</span>
          <span className="font-medium">{r.NomeCompleto}</span>
        </span>
      ) },
      { key: "TipoCliente", label: "Tipo Cliente", accessor: (r) => r.TipoCliente || r.ID_TipoCliente, mono: true },
      { key: "Telemovel", label: "Telemóvel", accessor: (r) => r.Telemovel || "—", mono: true },
      { key: "total", label: "Volume Encomendas", accessor: (r) => EUR(r.total), mono: true, align: "right" },
      { key: "nr_encomendas", label: "Nr. Encomendas", accessor: (r) => r.nr_encomendas, mono: true, align: "right" },
      { key: "Premio", label: "Prémio Atingido", accessor: (r) => r.PremioAtingido ? (
        <span className="inline-flex items-center gap-1.5">
          <span className="text-[10px] uppercase tracking-[0.1em] px-1.5 py-0.5 rounded bg-[#0F1829] text-white font-bold">
            {EUR(r.ValorPremio)}
          </span>
          <span className="text-xs text-[#4B5563] truncate max-w-[28rem]" title={r.PremioAtingido}>{r.PremioAtingido}</span>
        </span>
      ) : <span className="text-[#9CA3AF]">—</span> },
    ],
  },
  "active-subscriptions": {
    title: "Subscrições Activas",
    subtitle: "Clientes com subscrições activas (filtrável por ano/mês).",
    slug: "active-subscriptions",
    searchable: true,
    extraFilters: [
      { key: "year", label: "Ano", type: "number", placeholder: "YYYY" },
      { key: "month", label: "Mês", type: "number", placeholder: "1-12" },
    ],
    cols: [
      { key: "ID_Subscricao", label: "ID", accessor: (r) => r.ID_Subscricao, mono: true },
      { key: "NomeCompleto", label: "Nome", accessor: (r) => r.NomeCompleto },
      { key: "Email", label: "Email", accessor: (r) => r.Email },
      { key: "PackDescricao", label: "Pack", accessor: (r) => r.PackDescricao || `#${r.ID_Pack}` },
      { key: "DataInicio", label: "Início", accessor: (r) => r.DataInicio, mono: true },
      { key: "DataFim", label: "Fim", accessor: (r) => r.DataFim || "—", mono: true },
      { key: "DataUltimaExecucao", label: "Última", accessor: (r) => r.DataUltimaExecucao || "—", mono: true },
    ],
  },
};

export default function AdminReport({ reportKey }) {
  const cfg = REPORTS[reportKey];
  const loc = useLocation();
  const [paramValue, setParamValue] = useState("");
  const [search, setSearch] = useState("");
  const [extraValues, setExtraValues] = useState({});
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [summary, setSummary] = useState(null);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");

  // reset when switching pages/reports
  useEffect(() => {
    setParamValue(""); setSearch(""); setExtraValues({}); setRows([]); setTotal(0); setSummary(null); setPage(1); setErr("");
  }, [loc.pathname]);

  const canRun = cfg ? (!cfg.paramKey || !!paramValue) : false;

  const load = useCallback(async () => {
    if (!cfg || !canRun) return;
    setLoading(true); setErr("");
    try {
      const params = { page, page_size: pageSize };
      if (cfg.paramKey && paramValue) params[cfg.paramKey] = paramValue;
      if (cfg.searchable && search) params.search = search;
      if (cfg.extraFilters) {
        for (const f of cfg.extraFilters) {
          if (extraValues[f.key]) params[f.key] = extraValues[f.key];
        }
      }
      const r = await adminApi.report(cfg.slug, params);
      setRows(r.data || []); setTotal(r.total || 0); setSummary(r.summary || null);
    } catch (e) {
      setErr(e.message || "Erro"); setRows([]); setTotal(0); setSummary(null);
    } finally { setLoading(false); }
  }, [cfg, canRun, paramValue, page, pageSize, search, extraValues]);

  useEffect(() => { if (canRun) load(); }, [load, canRun]);

  if (!cfg) return <div className="p-6 text-sm text-red-700">Report {reportKey} não encontrado.</div>;

  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  const exportCsv = async () => {
    if (!canRun) return;
    const params = { page: 1, page_size: 5000 };
    if (cfg.paramKey && paramValue) params[cfg.paramKey] = paramValue;
    if (cfg.searchable && search) params.search = search;
    if (cfg.extraFilters) for (const f of cfg.extraFilters) if (extraValues[f.key]) params[f.key] = extraValues[f.key];
    const r = await adminApi.report(cfg.slug, params);
    const csv = toCsv(r.data || [], cfg.cols);
    downloadCsv(`${cfg.slug}-${new Date().toISOString().slice(0, 10)}.csv`, csv);
  };

  return (
    <div data-testid={`report-${reportKey}`}>
      <Link to="/admin" className="inline-flex items-center gap-1 text-xs text-[#978D78] hover:text-[#0F1829] mb-3">
        <ArrowLeft size={13} /> Voltar ao dashboard
      </Link>
      <div className="flex items-center gap-3 mb-5">
        <div className="w-11 h-11 grid place-items-center rounded-xl bg-[#0F1829] text-white"><List size={22} /></div>
        <div>
          <h1 className="text-2xl font-bold" style={{ fontFamily: "'Fraunces', serif" }}>{cfg.title}</h1>
          <p className="text-sm text-[#4B5563]">{cfg.subtitle}</p>
        </div>
      </div>

      <div className="bg-white border border-[#E6E1D6] rounded-md p-4 mb-3 flex flex-wrap items-end gap-3">
        {cfg.paramKey && (
          <div className="flex flex-col gap-1">
            <label className="text-[10px] uppercase tracking-[0.15em] text-[#978D78] font-bold">{cfg.paramLabel}</label>
            <EntityPicker entity={cfg.paramEntity} value={paramValue}
              onChange={(v) => { setPage(1); setParamValue(v); }}
              placeholder={`Pesquisar ${cfg.paramLabel.toLowerCase()}...`} testid={`picker-${cfg.paramEntity}`} />
          </div>
        )}
        {cfg.searchable && (
          <div className="flex flex-col gap-1">
            <label className="text-[10px] uppercase tracking-[0.15em] text-[#978D78] font-bold">Pesquisa</label>
            <div className="relative">
              <Search size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-[#978D78]" />
              <input value={search} onChange={(e) => { setPage(1); setSearch(e.target.value); }}
                placeholder="Nome, email..." data-testid="report-search-input"
                className="pl-8 pr-3 py-2 text-sm border border-[#E6E1D6] rounded w-72 outline-none focus:border-[#0F1829]" />
            </div>
          </div>
        )}
        {(cfg.extraFilters || []).map((f) => (
          <div key={f.key} className="flex flex-col gap-1">
            <label className="text-[10px] uppercase tracking-[0.15em] text-[#978D78] font-bold">{f.label}</label>
            {f.type === "entity" ? (
              <EntityPicker entity={f.entity} value={extraValues[f.key] || ""}
                onChange={(v) => { setPage(1); setExtraValues((s) => ({ ...s, [f.key]: v || "" })); }}
                placeholder={`Pesquisar ${f.label.toLowerCase()}...`} testid={`filter-${f.key}`} />
            ) : (
              <input type={f.type || "text"} placeholder={f.placeholder}
                value={extraValues[f.key] || ""}
                onChange={(e) => { setPage(1); setExtraValues((v) => ({ ...v, [f.key]: e.target.value })); }}
                data-testid={`filter-${f.key}`}
                className="px-3 py-2 text-sm border border-[#E6E1D6] rounded w-32 outline-none focus:border-[#0F1829]" />
            )}
          </div>
        ))}
        <div className="ml-auto flex items-end gap-2">
          <select value={pageSize} onChange={(e) => { setPage(1); setPageSize(Number(e.target.value)); }}
            className="px-2 py-2 text-sm border border-[#E6E1D6] rounded">
            <option value={12}>12/pg</option><option value={25}>25/pg</option><option value={50}>50/pg</option><option value={100}>100/pg</option>
          </select>
          <button onClick={exportCsv} disabled={!canRun || loading || rows.length === 0}
            data-testid="export-csv"
            className="px-3 py-2 text-sm border border-[#E6E1D6] rounded hover:bg-[#F5F2EB] disabled:opacity-40 flex items-center gap-1">
            <Download size={13} /> CSV
          </button>
        </div>
      </div>

      {err && <div className="mb-3 px-3 py-2 bg-red-50 border border-red-200 text-sm text-red-700 rounded">{err}</div>}

      {!canRun && (
        <div className="bg-[#FAF8F2] border border-dashed border-[#E6E1D6] rounded p-6 text-sm text-[#6B7280] text-center">
          Escolhe {cfg.paramLabel?.toLowerCase()} acima para ver resultados.
        </div>
      )}

      {canRun && summary && (
        <div className="bg-[#0F1829] text-white rounded-md px-4 py-3 mb-3 flex flex-wrap items-center gap-x-6 gap-y-2" data-testid="report-summary">
          <span className="text-[10px] uppercase tracking-[0.15em] text-white/60 font-bold">Resumo (exclui estados AP/CC/CP/PC)</span>
          {summary.inscritos !== undefined && (
            <span className="text-sm"><strong className="text-lg font-bold">{Number(summary.inscritos || 0).toLocaleString("pt-PT")}</strong> inscritos</span>
          )}
          {summary.total_valor !== undefined && (
            <span className="text-sm"><strong className="text-lg font-bold">{EUR(summary.total_valor || 0)}</strong> facturado</span>
          )}
        </div>
      )}

      {canRun && (
        <div className="bg-white border border-[#E6E1D6] rounded-md overflow-x-auto">
          <table className="text-sm" style={{ minWidth: "100%", width: "max-content" }}>
            <thead className="bg-[#F5F2EB] border-b border-[#E6E1D6]">
              <tr>
                {cfg.cols.map((c) => (
                  <th key={c.key} className={`px-3 py-2 text-[10px] uppercase tracking-[0.15em] text-[#978D78] ${c.align === "right" ? "text-right" : "text-left"}`}>
                    {c.label}
                  </th>
                ))}
                {cfg.rowActions && <th className="px-3 py-2 text-[10px] uppercase tracking-[0.15em] text-[#978D78] text-right">Acções</th>}
              </tr>
            </thead>
            <tbody>
              {loading && <tr><td colSpan={cfg.cols.length + (cfg.rowActions ? 1 : 0)} className="text-center py-8 text-[#6B7280]"><Loader2 size={16} className="inline animate-spin mr-1" /> A carregar...</td></tr>}
              {!loading && rows.length === 0 && <tr><td colSpan={cfg.cols.length + (cfg.rowActions ? 1 : 0)} className="text-center py-8 text-[#6B7280]">Sem resultados.</td></tr>}
              {!loading && rows.map((r, idx) => {
                const ignored = r.CountsAsInscrito === 0;
                return (
                  <tr key={`row-${idx}-${r.ID_Cliente || r.ID_Subscricao || idx}`}
                    className={`border-b border-[#F3F0E7] hover:bg-[#FAF8F2] ${ignored ? "text-[#9CA3AF] bg-[#FAF8F2]/60" : ""}`}
                    title={ignored ? "Estado não conta para inscritos" : undefined}>
                    {cfg.cols.map((c) => (
                      <td key={c.key} className={`px-3 py-2 ${c.mono ? "font-mono text-xs" : ""} ${c.align === "right" ? "text-right" : ""} ${ignored ? "line-through decoration-[#9CA3AF]/60" : ""} whitespace-nowrap`}>
                        {c.accessor(r)}
                      </td>
                    ))}
                    {cfg.rowActions && (
                      <td className="px-3 py-2 text-right whitespace-nowrap">
                        {cfg.rowActions({ row: r, refresh: load })}
                      </td>
                    )}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {canRun && (
        <div className="flex items-center justify-center gap-2 mt-3 text-sm">
          <span className="text-xs text-[#6B7280] mr-2">{total.toLocaleString("pt-PT")} resultados</span>
          <button onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page <= 1}
            className="p-1.5 rounded border border-[#E6E1D6] disabled:opacity-30 hover:bg-[#F5F2EB]"><ChevronLeft size={14} /></button>
          <span className="text-xs text-[#6B7280]">Pág {page} / {totalPages}</span>
          <button onClick={() => setPage((p) => Math.min(totalPages, p + 1))} disabled={page >= totalPages}
            className="p-1.5 rounded border border-[#E6E1D6] disabled:opacity-30 hover:bg-[#F5F2EB]"><ChevronRight size={14} /></button>
        </div>
      )}
    </div>
  );
}

function RegisterCommissionButton({ client, onDone }) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <button
        onClick={() => setOpen(true)}
        data-testid={`register-payment-btn-${client.ID_Cliente}`}
        className="inline-flex items-center gap-1 text-xs px-2.5 py-1 rounded border border-[#E6E1D6] hover:bg-[#0F1829] hover:text-white hover:border-[#0F1829] transition"
        title="Registar pagamento de comissão"
      >
        <Wallet size={12} /> Pagar
      </button>
      {open && (
        <RegisterCommissionPaymentModal
          client={client}
          onClose={() => setOpen(false)}
          onRegistered={() => { onDone && onDone(); }}
        />
      )}
    </>
  );
}

